import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';

const COLORS = ['#6366f1', '#8b5cf6', '#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#8b5a2b', '#db2777'];

interface RatingData {
  rating: number;
  count: number;
  percentage?: number;
}

interface ChoiceData {
  name: string;
  value: number;
  percentage?: number;
}

interface RatingChartProps {
  title: string;
  description?: string;
  data: RatingData[];
  height?: number;
}

export const RatingChart: React.FC<RatingChartProps> = ({
  title,
  description,
  data,
  height = 250
}) => {
  if (!data || data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          {description && <CardDescription>{description}</CardDescription>}
        </CardHeader>
        <CardContent>
          <div className="text-center text-muted-foreground py-6">
            No hay datos disponibles
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      <CardContent>
        <div style={{ height }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="rating"
                type="number"
                scale="point"
                domain={[1, 10]}
              />
              <YAxis />
              <Tooltip formatter={(value: any) => [value, 'Respuestas']} />
              <Bar dataKey="count" fill="#6366f1" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};

interface PieChartComponentProps {
  title: string;
  description?: string;
  data: ChoiceData[];
  height?: number;
  showPercentages?: boolean;
}

export const PieChartComponent: React.FC<PieChartComponentProps> = ({
  title,
  description,
  data,
  height = 250,
  showPercentages = true
}) => {
  if (!data || data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          {description && <CardDescription>{description}</CardDescription>}
        </CardHeader>
        <CardContent>
          <div className="text-center text-muted-foreground py-6">
            No hay datos disponibles
          </div>
        </CardContent>
      </Card>
    );
  }

  const total = data.reduce((sum, item) => sum + item.value, 0);
  const dataWithPercentages = data.map(item => ({
    ...item,
    percentage: Math.round((item.value / total) * 100)
  }));

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      <CardContent>
        <div style={{ height }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={dataWithPercentages}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={showPercentages ?
                  ({ name, percentage }) => `${name}: ${percentage}%` :
                  ({ name }) => name
                }
                outerRadius={height * 0.3}
                fill="#8884d8"
                dataKey="value"
              >
                {dataWithPercentages.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value: any, name: any) => [value, 'Respuestas']} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};

interface ProgressCardProps {
  title: string;
  value: number;
  total?: number;
  percentage?: number;
  description?: string;
  color?: string;
}

export const ProgressCard: React.FC<ProgressCardProps> = ({
  title,
  value,
  total,
  percentage,
  description,
  color = "#6366f1"
}) => {
  const displayPercentage = percentage ?? (total ? Math.round((value / total) * 100) : 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span>Completado</span>
            <span>{displayPercentage}%</span>
          </div>
          <Progress value={displayPercentage} className="h-2" />
          {description && (
            <p className="text-xs text-muted-foreground">{description}</p>
          )}
          {total && (
            <p className="text-xs text-muted-foreground">
              {value} de {total} respuestas
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

interface MetricCardProps {
  title: string;
  value: string | number;
  description?: string;
  icon?: React.ReactNode;
  color?: string;
}

export const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  description,
  icon,
  color = "#6366f1"
}) => {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        {icon && <div className="h-4 w-4 text-muted-foreground">{icon}</div>}
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold" style={{ color }}>{value}</div>
        {description && (
          <p className="text-xs text-muted-foreground">{description}</p>
        )}
      </CardContent>
    </Card>
  );
};

interface TextResponsesProps {
  title: string;
  responses: string[];
  maxDisplay?: number;
}

export const TextResponses: React.FC<TextResponsesProps> = ({
  title,
  responses,
  maxDisplay = 5
}) => {
  if (!responses || responses.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center text-muted-foreground py-6">
            No hay respuestas de texto disponibles
          </div>
        </CardContent>
      </Card>
    );
  }

  const displayResponses = responses.slice(0, maxDisplay);
  const remainingCount = responses.length - maxDisplay;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{responses.length} respuestas</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {displayResponses.map((response, index) => {
            // Asegurar que response sea un string
            const responseText = typeof response === 'object' && response !== null
              ? JSON.stringify(response)
              : String(response || '');

            return (
              <div key={index} className="p-3 border rounded-lg bg-gray-50">
                <p className="text-sm text-gray-700">{responseText}</p>
              </div>
            );
          })}
          {remainingCount > 0 && (
            <div className="text-center text-sm text-muted-foreground py-2">
              Y {remainingCount} respuestas más...
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};